import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ajaxconfig } from '../ajaxconfig';
import { BasedUrl } from '../serverurl';
function SubscriptionUsers() {
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'tokens'
  const navigate = useNavigate();
  const config = ajaxconfig();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(BasedUrl + 'subscription/users', config);
      console.log(response);
      setUsers(response.data.users || []);
      setTokens(response.data.active_tokens || []);
    } catch (error) {

      console.log(12345);
      console.log(error);
      toast.error('Failed to load subscription data');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setIsLoading(true);
    try {
      const response = await axios.post(BasedUrl + 'subscription/add-user', { email: newUserEmail }, config);
      console.log(response);
      toast.success(`Invitation sent to ${newUserEmail}`);
      setNewUserEmail('');
      fetchData();
    } catch (error) {

      console.log(error);
      toast.error(error.response?.data?.error || 'Failed to add user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(BasedUrl + 'subscription/generate-token', {}, config);
      toast.success('Token generated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate token');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeUser = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this user?')) return;

    try {
      await axios.delete(BasedUrl + `subscription/revoke/${id}`, config);
      toast.success('User revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke user');
    }
  };

  const handleRevokeToken = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) return;

    try {
      await axios.delete(BasedUrl + `subscription/revoke-token/${id}`, config);
      toast.success('Token revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke token');
    }
  };

  const handleToggleUserStatus = async (id, currentStatus) => {
    const endpoint = currentStatus === 'active' 
      ? BasedUrl + `subscription/deactivate/${id}`
      : BasedUrl + `subscription/reactivate/${id}`;
    
    try {
      await axios.post(endpoint, {}, config);
      toast.success(`User ${currentStatus === 'active' ? 'deactivated' : 'reactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <ToastContainer />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Subscription Users Management</h1>
        <p className="text-gray-600 mt-2">Manage users who share your subscription plan</p>
      </div>

      {/* Add User Form */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Add User to Subscription</h2>
        <form onSubmit={handleAddUser} className="flex gap-4">
          <input
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder="Enter user's email address"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          <button
            type="submit"
            disabled={isLoading}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Send Invitation'}
          </button>
        </form>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('users')}
        >
          Users ({users.length})
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'tokens' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('tokens')}
        >
          Share Tokens ({tokens.length})
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No users added yet. Invite users by email or share a token.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.user_name || user.email}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-800'
                            : user.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.joined_at ? new Date(user.joined_at).toLocaleDateString() : 'Not joined yet'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {user.status === 'active' ? (
                          <button
                            onClick={() => handleToggleUserStatus(user.id, 'active')}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Deactivate
                          </button>
                        ) : user.status === 'inactive' ? (
                          <button
                            onClick={() => handleToggleUserStatus(user.id, 'inactive')}
                            className="text-green-600 hover:text-green-900"
                          >
                            Reactivate
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleRevokeUser(user.id)}
                          className="text-red-600 hover:text-red-900 ml-4"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">Shareable Tokens</h3>
            <button
              onClick={handleGenerateToken}
              disabled={isLoading}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg font-semibold"
            >
              Generate New Token
            </button>
          </div>
          
          <div className="space-y-4">
            {tokens.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active tokens. Generate one to share.</p>
            ) : (
              tokens.map((token) => (
                <div key={token.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono">
                          {token.token}
                        </code>
                        <button
                          onClick={() => copyToClipboard(token.token, 'Token')}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-sm text-gray-500">
                        Expires: {new Date(token.expires_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRevokeToken(token.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-800 mb-2">How it works:</h3>
        <ul className="list-disc list-inside text-blue-700 space-y-1">
          <li>Users can join your subscription via email invitation or token</li>
          <li>Each user counts towards your subscription limit</li>
          <li>You can deactivate users anytime to temporarily suspend their access</li>
          <li>Revoked users are permanently removed from your subscription</li>
          <li>Tokens expire after 7 days and can be revoked anytime</li>
        </ul>
      </div>

      
      <div className="mt-8">
  <div className="flex flex-col sm:flex-row gap-4">
    {/* WhatsApp Button - Full width on mobile, auto on larger screens */}
    <a
      href="https://wa.me/2348113315252"
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
    >
      <i className="fab fa-whatsapp text-lg sm:text-xl"></i>
      <span className="text-sm sm:text-base md:text-lg lg:text-base">
        Chat on WhatsApp
      </span>
    </a>

    {/* Back to Payment Button - Full width on mobile, auto on larger screens */}
    <button
      onClick={() => navigate('/cbt_creator/payment')}
      className="group flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
    >
      <i className="fas fa-arrow-left text-lg sm:text-xl"></i>
      <span className="text-sm sm:text-base md:text-lg lg:text-base">
        Back to Payment
      </span>
    </button>
  </div>
</div>
{/* end */}

    </div>
  );
}

export default SubscriptionUsers;